import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import logo1 from "../images/QS_Logo.png";

const NAV_ITEMS = [
  { title: "Home", path: "/" },
  { title: "About Us", path: "/about" },
  {
    title: "Product & Services",
    path: "/services-product",
  },
  { title: "Career", path: "/career" },
  { title: "Contact Us", path: "/contact" },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const handleServiceToggle = () => setServicesOpen((open) => !open);
  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
  };

  const businessNumber = "918106938625"; // Add business number for SEO

  return (
    <>
      {/* SEO / Structured Data */}
      <Helmet>
        <title>Quadrangle Solutions | Shipping & Logistics Software</title>
        <meta
          name="description"
          content="Quadrangle Solutions provides AI-driven software solutions for shipping, logistics, and allied industries. Streamline operations and accelerate growth."
        />
        <meta
          name="keywords"
          content="AI, logistics, shipping, software, solutions"
        />
        <link rel="canonical" href="https://www.quadranglesolutions.com" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Quadrangle Solutions",
            url: "https://www.quadranglesolutions.com",
            logo: "https://www.quadranglesolutions.com/logo.png",
            sameAs: [
              "https://www.facebook.com/quadranglesolutions",
              "https://www.linkedin.com/company/quadranglesolutions",
              "https://twitter.com/quadranglesolutions",
            ],
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: `+${businessNumber}`,
                contactType: "customer service",
                areaServed: "IN",
              },
            ],
          })}
        </script>
      </Helmet>

      <nav className="bg-white sticky top-0 z-50 shadow-md border-b border-gray-200">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 flex items-center justify-between h-24">
          {/* Logo */}
          <Link
            to="/"
            aria-label="Quadrangle Solutions Home"
            onClick={handleNavLinkClick}
            className="flex items-center"
          >
            <div className="rounded-md">
              <img
                src={logo1}
                alt="Quadrangle Solutions Logo"
                className="h-[9rem] w-[14rem] object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex space-x-8 items-center">
            {NAV_ITEMS.map((item, idx) => (
              <li key={idx} className="relative group">
                {item.submenu ? (
                  <>{/* Submenu code */}</>
                ) : (
                  <Link
                    to={item.path}
                    className={`font-medium ${
                      isActive(item.path)
                        ? "text-green-600"
                        : "text-gray-700 hover:text-green-600"
                    } transition`}
                    onClick={handleNavLinkClick}
                  >
                    {item.title}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <Link
              to="/contact"
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-md px-6 py-3 shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-300 hover:scale-105"
              onClick={handleNavLinkClick}
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen((m) => !m)}
              aria-label="Toggle Navigation Menu"
              aria-expanded={mobileMenuOpen}
              className="text-gray-700 p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {mobileMenuOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.21 }}
              className="md:hidden bg-white border-t border-gray-200 shadow-lg overflow-hidden"
            >
              <ul className="flex flex-col px-6 py-4 space-y-4">
                {NAV_ITEMS.map((item, idx) => (
                  <li key={idx}>
                    <Link
                      to={item.path}
                      className={`font-medium ${
                        isActive(item.path)
                          ? "text-green-600"
                          : "text-gray-700 hover:text-green-600"
                      }`}
                      onClick={handleNavLinkClick}
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}

                {/* Mobile CTA */}
                <li className="pt-4 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="block text-center bg-green-600 rounded-md px-6 py-3 font-semibold text-white hover:bg-green-700 transition"
                    onClick={handleNavLinkClick}
                  >
                    Get Quote
                  </Link>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
